import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Invoices() {
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const profileId = activeProfileId || profiles[0]?.id;
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!profileId) { setInvoices([]); setClients([]); return; }
    const [inv, cl] = await Promise.all([
      api.get(`/invoices?profile_id=${profileId}`),
      api.get(`/clients?profile_id=${profileId}`),
    ]);
    setInvoices(inv.data);
    setClients(cl.data);
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice deleted");
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 lg:mb-12">
        <div>
          {profiles.length > 1 && (
            <Select value={profileId} onValueChange={setActiveProfileId}>
              <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-56 mb-3 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Records</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Invoices</h1>
        </div>
        <Button
          data-testid="create-invoice-top-btn"
          onClick={() => navigate("/invoices/new")}
          className="rounded-none bg-white text-black hover:bg-neutral-200 font-sans px-6 py-5 w-full sm:w-auto"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
          New Invoice
        </Button>
      </div>

      <div className="border border-white/10">
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          <div className="col-span-2">Invoice No</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2 text-right">Balance Due</div>
          <div className="col-span-1 text-right">—</div>
        </div>
        {invoices.length === 0 ? (
          <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">
            No invoices yet
          </div>
        ) : (
          invoices.map((inv) => (
            <Link
              key={inv.id}
              to={`/invoices/${inv.id}`}
              data-testid={`invoice-row-${inv.invoice_number}`}
              className="block md:grid md:grid-cols-12 gap-4 px-4 py-4 md:py-5 md:items-center border-b border-white/10 hover:bg-white/[0.02] transition-colors"
            >
              {/* Mobile card layout */}
              <div className="flex md:hidden items-start justify-between mb-1">
                <div>
                  <div className="font-mont text-sm text-[#D4AF37]">{inv.invoice_number}</div>
                  <div className="text-sm text-white mt-0.5">{clientName(inv.client_id)}</div>
                  <div className="font-mont text-xs text-neutral-500 mt-1">{inv.issue_date}</div>
                </div>
                <button
                  data-testid={`delete-invoice-${inv.invoice_number}`}
                  onClick={(e) => remove(inv.id, e)}
                  className="text-neutral-600 hover:text-red-400 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex md:hidden items-center justify-between font-mont text-sm">
                <span className="text-white">{fmtINR(inv.totals?.grand_total)}</span>
                <span className={inv.totals?.balance_due <= 0 ? "text-emerald-400" : "text-[#D4AF37]"}>
                  Due: {fmtINR(inv.totals?.balance_due)}
                </span>
              </div>

              {/* Desktop grid layout */}
              <div className="hidden md:block col-span-2 font-mont text-sm text-[#D4AF37]">{inv.invoice_number}</div>
              <div className="hidden md:block col-span-3 text-sm text-white truncate">{clientName(inv.client_id)}</div>
              <div className="hidden md:block col-span-2 font-mont text-xs text-neutral-400">{inv.issue_date}</div>
              <div className="hidden md:block col-span-2 text-right font-mont text-sm text-white">{fmtINR(inv.totals?.grand_total)}</div>
              <div className="hidden md:block col-span-2 text-right font-mont text-sm">
                <span className={inv.totals?.balance_due <= 0 ? "text-emerald-400" : "text-[#D4AF37]"}>
                  {fmtINR(inv.totals?.balance_due)}
                </span>
              </div>
              <div className="hidden md:flex col-span-1 justify-end">
                <button
                  data-testid={`delete-invoice-${inv.invoice_number}-desktop`}
                  onClick={(e) => remove(inv.id, e)}
                  className="text-neutral-600 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
